import React, { ChangeEvent } from "react";
import styled, { useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { openMessage } from "utils/misc";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import Modal from "common/Modal/Modal";
import TextArea from "common/Inputs/TextArea";
import {
  MAX_REVIEW_LENGTH,
  ReviewStep,
  useLeaveReviewModal,
} from "@shared/common/Nps/useLeaveReviewModal";
import {
  DarkPositiveFeedbackIcon,
  PositiveFeedbackIcon,
} from "common/Icons/Icons";
import { requestLeaveReview } from "services/webview/webViewBridgeUtils.ts";
import { IS_WEB_VIEW } from "common/constants.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

const LeaveReviewModal = () => {
  const theme = useTheme();
  const { isWhitelabel } = useBranding();
  const {
    buttons,
    isOpen,
    rating,
    setRating,
    step,
    review,
    setReview,
    isReviewOverLength,
    footerData,
    isGoodRating,
  } = useLeaveReviewModal({
    openMessage,
    redirectToTrustPilot: () =>
      window.open("https://www.trustpilot.com/evaluate/cornix.io", "_blank"),
    requestLeaveReview,
    isMobile: IS_WEB_VIEW,
  });

  const getRatingButton = (index: number) => {
    const selected = rating === index;
    return (
      <StyledButton
        onClick={() => setRating(index)}
        $selected={selected}
        key={index}
      >
        <ButtonText $selected={selected}>{index}</ButtonText>
      </StyledButton>
    );
  };

  const getTitleText = (text: string, secondary?: boolean) => {
    return (
      <Text
        style={{
          fontSize: theme.typography.pxFontSizes.L,
          fontWeight: "500",
          textAlign: "center",
          maxWidth: 317,
        }}
        type={secondary ? "secondary" : undefined}
      >
        {text}
      </Text>
    );
  };

  const getContent = () => {
    if (isWhitelabel) return null;

    if (step === ReviewStep.RATING) {
      return (
        <>
          <TextWrapper>
            {getTitleText(
              `How likely are you to recommend ${
                isWhitelabel ? "our service" : "Cornix"
              } to a friend or colleague?`
            )}
          </TextWrapper>
          <ButtonsRowsWrapper>
            <ButtonsRow>
              {buttons[0].map((rating) => getRatingButton(rating))}
            </ButtonsRow>
            <ButtonsRow style={{ marginLeft: 4, marginTop: 8 }}>
              {buttons[1].map((rating) => getRatingButton(rating))}
            </ButtonsRow>
          </ButtonsRowsWrapper>
          <Flex style={{ marginTop: 16 }}>
            <Text type="secondary">0 - Not likely at all</Text>
            <Text type="secondary">10 - Very Likely</Text>
          </Flex>
        </>
      );
    } else if (isGoodRating && !IS_WEB_VIEW) {
      const Icon = theme.isDarkMode
        ? DarkPositiveFeedbackIcon
        : PositiveFeedbackIcon;
      return (
        <Flex $flexDirection="column" $alignItems="center">
          <Icon $size={120} style={{ marginBottom: 12 }} />
          {getTitleText("Thank you for your positive feedback!")}
          <Text style={{ marginTop: 16 }}>
            {`We're glad to hear you're enjoying ${
              isWhitelabel ? "our service" : "Cornix"
            }. Would you be willing to share your experience with others? A quick review on Trustpilot would mean a lot to us and help others discover the benefits of ${
              isWhitelabel ? "our platform" : "Cornix"
            }.`}
          </Text>
        </Flex>
      );
    }
    return (
      <div>
        <Flex $flexDirection="column" $alignItems="center">
          {getTitleText("What can we do to improve?")}
          {getTitleText(" (Optional)", true)}
        </Flex>
        <div style={{ marginTop: 30 }}>
          <TextArea
            placeholder="We’d love to hear your suggestions"
            style={{ height: 130, paddingTop: 8, maxHeight: 250 }}
            rows={7}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setReview(e.target.value)
            }
            value={review}
          />
          <Text
            $size="S"
            style={{
              textAlign: "right",
              marginTop: 4,
              color: isReviewOverLength
                ? theme.colors.red
                : theme.colors.midDark,
            }}
          >
            {review?.length}/{MAX_REVIEW_LENGTH}
          </Text>
        </div>
      </div>
    );
  };

  const getFooter = () => {
    return (
      <Flex $justifyContent="flex-end">
        <Button
          onClick={() => footerData.onCancel()}
          type="link"
          style={{ marginRight: 8 }}
        >
          {footerData.cancelText}
        </Button>
        <Button
          type="primary"
          disabled={footerData.okDisabled}
          onClick={() => footerData.onOk()}
        >
          {footerData.okText}
        </Button>
      </Flex>
    );
  };

  return (
    <Modal
      open={isOpen}
      footer={getFooter()}
      onCancel={() => footerData.onCancel()}
      maskClosable={false}
    >
      {getContent()}
    </Modal>
  );
};

export default LeaveReviewModal;

const TextWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ButtonsRowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
`;
const ButtonsRow = styled.div`
  display: flex;
  column-gap: 16px;
  @media (max-width: 385px) {
    column-gap: 10px;
  }
`;
const StyledButton = styled.div<{ $selected?: boolean }>`
  height: 42px;
  width: 42px;
  border-radius: 21px;
  background-color: ${(p) =>
    p.$selected
      ? p.theme.colors.primaryBlue
      : hexToRGB(p.theme.colors.primaryBlue, 0.12)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const ButtonText = styled(Text)<{ $selected?: boolean }>`
  color: ${(p) =>
    p.$selected ? p.theme.colors.buttonText : p.theme.colors.primaryBlue};
  font-size: ${(p) => p.theme.typography.pxFontSizes.L}px;
`;
